import { Alert, Badge, Card, Table } from "react-bootstrap";
import type {
  CompletionAccountDetail,
  CompletionData,
  CompletionResidualModel,
} from "../lib/explainTypes";
import { useDisplayMode } from "../hooks/useDisplayMode";
import { formatCurrency, formatPercent } from "../../../lib/numbers";

interface Props {
  data: CompletionData;
  resolveAccountName: (accountId: number) => string;
}

const num = (s: string | null | undefined): number =>
  s == null || s === "" ? 0 : Number(s);

// Sorted union of asset-class keys across one or more dollar maps —
// keeps progression / detail tables column-stable regardless of the
// per-account payload ordering.
function assetClassKeys(...maps: Record<string, string>[]): string[] {
  const keys = new Set<string>();
  for (const m of maps) for (const k of Object.keys(m)) keys.add(k);
  return [...keys].sort();
}

// $-mode shows raw dollars; %-mode shows the share of `total`. `total`
// of 0 falls back to dollars so an empty pool still reads sensibly.
function DollarOrPercent({ value, total }: { value: number; total: number }) {
  const { mode } = useDisplayMode();
  if (mode === "dollar" || total <= 0) return <>{formatCurrency(value)}</>;
  return <>{formatPercent(value / total, 2)}</>;
}

// (1) Strategy summary — narrative lines + processing-order roster.
function StrategySummary({
  data,
  resolveAccountName,
}: {
  data: CompletionData;
  resolveAccountName: (accountId: number) => string;
}) {
  return (
    <section className="mb-4">
      <h6>Strategy summary</h6>
      {data.summary.length > 0 && (
        <ul className="mb-3">
          {data.summary.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      )}
      <Table size="sm" responsive className="mb-0">
        <thead>
          <tr>
            <th>Account</th>
            <th>Processing group</th>
            <th className="text-end">Balance</th>
            <th className="text-end">Order</th>
            <th>Order reason</th>
          </tr>
        </thead>
        <tbody>
          {data.account_classifications.map((c) => (
            <tr key={c.account_id}>
              <td>{resolveAccountName(c.account_id)}</td>
              <td>{c.processing_group}</td>
              <td className="text-end">{formatCurrency(num(c.balance))}</td>
              <td className="text-end">{c.processing_order}</td>
              <td>{c.processing_order_reason}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </section>
  );
}

// (2) Verification banner. `passed === false` is expected, explanatory
// content (constraints prevented reaching target) — rendered as an
// informational `info` alert, never a `danger` state.
function VerificationBanner({
  data,
  resolveAccountName,
}: {
  data: CompletionData;
  resolveAccountName: (accountId: number) => string;
}) {
  const { verification } = data;
  const maxDeviation = num(verification.max_deviation);
  return (
    <section className="mb-4">
      <h6>Verification</h6>
      <Alert
        variant={verification.passed ? "success" : "info"}
        className="mb-2"
      >
        {verification.passed ? (
          <>
            Household reached target — max deviation{" "}
            {formatPercent(maxDeviation, 2)}.
          </>
        ) : (
          <>
            Constraints prevented the household from fully reaching target — max
            deviation {formatPercent(maxDeviation, 2)}. The positions below
            could not be adjusted further; this is expected behaviour, not an
            error.
          </>
        )}
      </Alert>
      {!verification.passed && verification.constraint_impacts.length > 0 && (
        <Table size="sm" responsive className="mb-0">
          <thead>
            <tr>
              <th>Account</th>
              <th>Symbol</th>
              <th className="text-end">Target weight</th>
              <th className="text-end">Actual weight</th>
              <th className="text-end">Deviation</th>
              <th>Cause</th>
            </tr>
          </thead>
          <tbody>
            {verification.constraint_impacts.map((impact, i) => (
              <tr key={`${impact.account_id}-${impact.symbol}-${i}`}>
                <td>{resolveAccountName(impact.account_id)}</td>
                <td>{impact.symbol}</td>
                <td className="text-end">
                  {formatPercent(num(impact.target_weight), 2)}
                </td>
                <td className="text-end">
                  {formatPercent(num(impact.actual_weight), 2)}
                </td>
                <td className="text-end">
                  {formatPercent(num(impact.deviation), 2)}
                </td>
                <td>{impact.cause}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
}

// (3) Constrained-pool progression — cumulative immutable dollars after
// each account, broken out by risk level and asset class.
function ConstrainedPoolProgression({
  data,
  resolveAccountName,
}: {
  data: CompletionData;
  resolveAccountName: (accountId: number) => string;
}) {
  if (data.constrained_pool_progression.length === 0) return null;
  const acKeys = assetClassKeys(
    ...data.constrained_pool_progression.map((e) => e.by_asset_class),
  );
  const riskKeys = [
    ...new Set(
      data.constrained_pool_progression.flatMap((e) =>
        Object.keys(e.by_risk_level),
      ),
    ),
  ].sort();
  return (
    <section className="mb-4">
      <h6>Constrained-pool progression</h6>
      <Table size="sm" responsive className="mb-0">
        <thead>
          <tr>
            <th>After account</th>
            <th className="text-end">Total constrained</th>
            {riskKeys.map((r) => (
              <th key={r} className="text-end">
                {r}
              </th>
            ))}
            {acKeys.map((ac) => (
              <th key={ac} className="text-end">
                {ac}
              </th>
            ))}
            <th className="text-end">Symbols</th>
          </tr>
        </thead>
        <tbody>
          {data.constrained_pool_progression.map((entry) => {
            const total = num(entry.total_constrained_amount);
            return (
              <tr key={entry.after_account_id}>
                <td>{resolveAccountName(entry.after_account_id)}</td>
                <td className="text-end">{formatCurrency(total)}</td>
                {riskKeys.map((r) => (
                  <td key={r} className="text-end">
                    <DollarOrPercent
                      value={num(entry.by_risk_level[r])}
                      total={total}
                    />
                  </td>
                ))}
                {acKeys.map((ac) => (
                  <td key={ac} className="text-end">
                    <DollarOrPercent
                      value={num(entry.by_asset_class[ac])}
                      total={total}
                    />
                  </td>
                ))}
                <td className="text-end">{entry.by_symbol_count}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </section>
  );
}

// (4) One residual-model block: the three residual_calculation tables
// for a no-model account, annotated with scaling and exclusions.
function ResidualModelBlock({
  model,
  accountName,
}: {
  model: CompletionResidualModel;
  accountName: string;
}) {
  const { residual_calculation: calc } = model;
  return (
    <div className="mb-4">
      <div className="d-flex align-items-center gap-2 mb-2">
        <strong>{accountName}</strong>
        {model.scaled ? (
          <Badge bg="warning">
            Scaled
            {model.scale_factor != null
              ? ` ×${formatDecimal4(model.scale_factor)}`
              : ""}
          </Badge>
        ) : (
          <Badge bg="secondary">No scaling needed</Badge>
        )}
        {model.excluded_symbols.length > 0 && (
          <Badge bg="info">Excluded: {model.excluded_symbols.join(", ")}</Badge>
        )}
      </div>

      <div className="mb-2">
        <small className="text-muted">Risk-level waterfall</small>
        <Table size="sm" responsive className="mb-0">
          <thead>
            <tr>
              <th>Risk level</th>
              <th className="text-end">Ideal</th>
              <th className="text-end">Constrained</th>
              <th className="text-end">Residual</th>
            </tr>
          </thead>
          <tbody>
            {calc.risk_level_rows.map((row, i) => (
              <tr key={`${row.risk_level}-${i}`}>
                <td>{row.risk_level}</td>
                <td className="text-end">{formatCurrency(num(row.ideal))}</td>
                <td className="text-end">
                  {formatCurrency(num(row.constrained))}
                </td>
                <td className="text-end">
                  {formatCurrency(num(row.residual))}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="mb-2">
        <small className="text-muted">Asset-class waterfall</small>
        <Table size="sm" responsive className="mb-0">
          <thead>
            <tr>
              <th>Risk level</th>
              <th>Asset class</th>
              <th className="text-end">Ideal</th>
              <th className="text-end">Constrained</th>
              <th className="text-end">Waterfall allocated</th>
              <th className="text-end">Residual</th>
            </tr>
          </thead>
          <tbody>
            {calc.ac_level_rows.map((row, i) => (
              <tr key={`${row.risk_level}-${row.asset_class}-${i}`}>
                <td>{row.risk_level}</td>
                <td>{row.asset_class}</td>
                <td className="text-end">{formatCurrency(num(row.ideal))}</td>
                <td className="text-end">
                  {formatCurrency(num(row.constrained))}
                </td>
                <td className="text-end">
                  {formatCurrency(num(row.waterfall_allocated))}
                </td>
                <td className="text-end">
                  {formatCurrency(num(row.residual))}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div>
        <small className="text-muted">Security-level mapping</small>
        <Table size="sm" responsive className="mb-0">
          <thead>
            <tr>
              <th>Symbol</th>
              <th className="text-end">Model weight</th>
              <th>Asset class</th>
              <th className="text-end">Target $</th>
              <th className="text-end">Constrained $</th>
              <th className="text-end">Waterfall $</th>
              <th className="text-end">Residual $</th>
            </tr>
          </thead>
          <tbody>
            {calc.security_mapping_rows.map((row, i) => (
              <tr key={`${row.symbol}-${i}`}>
                <td>{row.symbol}</td>
                <td className="text-end">
                  {formatPercent(num(row.model_weight), 2)}
                </td>
                <td>{row.asset_class}</td>
                <td className="text-end">
                  {formatCurrency(num(row.target_dollars))}
                </td>
                <td className="text-end">
                  {formatCurrency(num(row.constrained_dollars))}
                </td>
                <td className="text-end">
                  {formatCurrency(num(row.waterfall_dollars))}
                </td>
                <td className="text-end">
                  {formatCurrency(num(row.residual))}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

// scale_factor is a raw float string (e.g. "0.8872336639"); show 4 dp.
function formatDecimal4(s: string): string {
  return num(s).toFixed(4);
}

// (5) Account-detail block — role, household share, and the
// household / constrained / residual risk split per account.
function AccountDetailBlock({
  detail,
  accountName,
}: {
  detail: CompletionAccountDetail;
  accountName: string;
}) {
  const riskKeys = [
    ...new Set([
      ...Object.keys(detail.household_risk),
      ...Object.keys(detail.constrained_risk),
      ...Object.keys(detail.residual_risk),
    ]),
  ].sort();
  return (
    <div className="mb-3">
      <div className="d-flex align-items-center gap-2 mb-2">
        <strong>{accountName}</strong>
        <Badge bg="secondary">{detail.role}</Badge>
        <span className="text-muted">
          {formatPercent(num(detail.pct_of_household), 1)} of household ·{" "}
          {formatCurrency(num(detail.balance))}
        </span>
      </div>
      <Table size="sm" responsive className="mb-0">
        <thead>
          <tr>
            <th>Risk level</th>
            <th className="text-end">Household</th>
            <th className="text-end">Constrained</th>
            <th className="text-end">Residual</th>
          </tr>
        </thead>
        <tbody>
          {riskKeys.map((r) => (
            <tr key={r}>
              <td>{r}</td>
              <td className="text-end">
                {formatPercent(num(detail.household_risk[r]), 2)}
              </td>
              <td className="text-end">
                {formatPercent(num(detail.constrained_risk[r]), 2)}
              </td>
              <td className="text-end">
                {formatPercent(num(detail.residual_risk[r]), 2)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// v3-native completion-strategy panel. Renders the completion
// `v3:model_assignment` payload for `strategy` "completion" and
// "soft_completion" (the soft-completion payload is a CompletionData
// superset). Replaces the legacy `CompletionPlaceholder` JSON stub.
// See s-completion-strategy-explain-019e4107 §3.
export default function CompletionStrategyPanel({
  data,
  resolveAccountName,
}: Props) {
  const residualModels = Object.values(data.residual_models);
  const accountDetails = Object.values(data.account_details);
  return (
    <Card className="mb-3">
      <Card.Header>
        Completion Strategy — {formatCurrency(num(data.household_aum))}{" "}
        household AUM
      </Card.Header>
      <Card.Body>
        <StrategySummary data={data} resolveAccountName={resolveAccountName} />
        <VerificationBanner
          data={data}
          resolveAccountName={resolveAccountName}
        />
        <ConstrainedPoolProgression
          data={data}
          resolveAccountName={resolveAccountName}
        />

        {residualModels.length > 0 && (
          <section className="mb-4">
            <h6>Per-account residual models</h6>
            {residualModels.map((model) => (
              <ResidualModelBlock
                key={model.account_id}
                model={model}
                accountName={resolveAccountName(model.account_id)}
              />
            ))}
          </section>
        )}

        {accountDetails.length > 0 && (
          <section>
            <h6>Account detail</h6>
            {accountDetails.map((detail) => (
              <AccountDetailBlock
                key={detail.account_id}
                detail={detail}
                accountName={resolveAccountName(detail.account_id)}
              />
            ))}
          </section>
        )}
      </Card.Body>
    </Card>
  );
}
